import React from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  EmojiEvents as JackpotIcon,
  LocalFireDepartment as StreakIcon,
  MilitaryTech as MegaIcon,
  Refresh as RefreshIcon,
  Stars as MiniIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { useGetJackpotOverviewQuery } from '../../store/api/jackpotApi';

const formatCurrency = (value) =>
  `K${Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDateTime = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleString();
};

const StatCard = ({ title, value, subtitle, icon: Icon, color = 'primary' }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight="bold" color={`${color}.main`}>
            {value}
          </Typography>
          {subtitle ? (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        <Box
          sx={{
            backgroundColor: `${color}.light`,
            borderRadius: 2,
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon sx={{ color: `${color}.main` }} />
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const buildRows = (players = []) => players.map((player) => ({ id: player._id, ...player }));

const playerColumns = [
  {
    field: 'phoneNumber',
    headerName: 'Phone',
    flex: 1,
    minWidth: 150,
    renderCell: (params) => (
      <Typography variant="body2" fontFamily="monospace">
        {params.value}
      </Typography>
    ),
  },
  {
    field: 'consecutiveWins',
    headerName: 'Current Streak',
    minWidth: 130,
    type: 'number',
    renderCell: (params) => (
      <Chip
        label={`${params.value || 0} wins`}
        size="small"
        color={params.value >= 10 ? 'warning' : 'success'}
      />
    ),
  },
  {
    field: 'longestWinStreak',
    headerName: 'Best Streak',
    minWidth: 120,
    type: 'number',
  },
  {
    field: 'winRate',
    headerName: 'Win Rate',
    minWidth: 100,
    type: 'number',
    renderCell: (params) => `${params.value || 0}%`,
  },
  {
    field: 'totalWinnings',
    headerName: 'Total Winnings',
    minWidth: 140,
    type: 'number',
    renderCell: (params) => formatCurrency(params.value),
  },
  {
    field: 'totalGamesPlayed',
    headerName: 'Games',
    minWidth: 90,
    type: 'number',
  },
  {
    field: 'lastPlayedAt',
    headerName: 'Last Played',
    flex: 1,
    minWidth: 180,
    renderCell: (params) => formatDateTime(params.value),
  },
];

const Jackpot = () => {
  const { data, isLoading, isFetching, refetch } = useGetJackpotOverviewQuery();

  const overview = data?.data || {};
  const summary = overview.summary || {};
  const thresholds = overview.thresholds || { miniJackpot: 5, megaJackpot: 10 };

  const miniRows = buildRows(overview.miniJackpotPlayers);
  const megaRows = buildRows(overview.megaJackpotPlayers);
  const leaderboardRows = buildRows(overview.streakLeaderboard);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Jackpot Tracking
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor current jackpot pool and players on mini and mega win streaks.
          </Typography>
        </Box>
        <Tooltip title="Refresh Data">
          <IconButton onClick={refetch} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {(isLoading || isFetching) ? <LinearProgress sx={{ mb: 2 }} /> : null}

      <Alert severity="info" sx={{ mb: 3 }}>
        Mini jackpot requires at least {thresholds.miniJackpot} consecutive wins. Mega jackpot
        requires at least {thresholds.megaJackpot} consecutive wins.
      </Alert>

      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Current Jackpot Pool"
            value={formatCurrency(overview.jackpotPool)}
            subtitle={
              overview.jackpotUpdatedAt
                ? `Updated ${formatDateTime(overview.jackpotUpdatedAt)}`
                : 'No jackpot updates yet'
            }
            icon={JackpotIcon}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Mini Jackpot Players"
            value={summary.miniQualifiedPlayers || 0}
            subtitle={`${thresholds.miniJackpot}-${thresholds.megaJackpot - 1} consecutive wins`}
            icon={MiniIcon}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Mega Jackpot Players"
            value={summary.megaQualifiedPlayers || 0}
            subtitle={`${thresholds.megaJackpot}+ consecutive wins`}
            icon={MegaIcon}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Highest Current Streak"
            value={`${summary.highestCurrentStreak || 0} wins`}
            subtitle={`Best ever streak: ${summary.highestHistoricalStreak || 0}`}
            icon={StreakIcon}
            color="info"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Mini Jackpot Watchlist
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Players currently on {thresholds.miniJackpot}-{thresholds.megaJackpot - 1} consecutive wins.
            </Typography>
            <DataGrid
              autoHeight
              rows={miniRows}
              columns={playerColumns}
              disableRowSelectionOnClick
              hideFooter
              loading={isLoading}
              sx={{ border: 0 }}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Mega Jackpot Qualifiers
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Players currently at {thresholds.megaJackpot}+ consecutive wins.
            </Typography>
            <DataGrid
              autoHeight
              rows={megaRows}
              columns={playerColumns}
              disableRowSelectionOnClick
              hideFooter
              loading={isLoading}
              sx={{ border: 0 }}
            />
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Streak Leaderboard
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Top tracked players by current consecutive wins.
        </Typography>
        <DataGrid
          autoHeight
          rows={leaderboardRows}
          columns={playerColumns}
          disableRowSelectionOnClick
          hideFooter
          loading={isLoading}
          sx={{ border: 0 }}
        />
      </Paper>
    </Box>
  );
};

export default Jackpot;
